import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  useDisclosure,
  GridItem,
  Text,
  Container,
  Spinner,
  AspectRatio,
  Box,
} from '@chakra-ui/react';
import SimpleSlider from '@rsc/ui-components/dept/molecules/SimpleSlider';
import SocialLinksMessage from '@rsc/ui-components/dept/atoms/SocialLinksMessage';
import { DeptGrid } from '@rsc/ui-components/dept/design-system';
import useConvertToObjectArray from '@rsc/ui-components/dept/utils/useConvertToObjectArray';
import { generateSectionId } from '@rsc/ui-components/dept/utils/generateSectionId';
import ReactPlayer from 'react-player';
import DrawerModal from '@rsc/ui-components/dept/molecules/DrawerModal';
import SocialGridItem from './SocialGridItem';

export default function SocialGrid({ block, socialMedia }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(true);
  const [currentVideo, setCurrentVideo] = useState(null);

  useEffect(() => {
    setIsLoading(true);
  }, [onOpen]);

  const handleClick = video => {
    setIsLoading(true);
    if (video._key !== currentVideo?._key) setCurrentVideo(video);
    onOpen();
  };

  const socialData = useConvertToObjectArray(socialMedia);

  const clickableSliderItem = item =>
    SocialGridItem({
      block: item,
      handleClick,
    });

  return (
    <Container {...generateSectionId(block)} pt={20} pb={14}>
      <DeptGrid>
        <GridItem colSpan={[6, null, 4]} textAlign={['center', null, 'left']}>
          <Text textStyle="h2" fontWeight="extrabold" mb={[4, null, 6]}>
            {block.headline}
          </Text>
        </GridItem>
      </DeptGrid>
      <SocialLinksMessage data={socialData} />
      <SimpleSlider
        items={block.cards}
        sliderItem={clickableSliderItem}
        mt={10}
        display={['block', null, 'none']}
      />
      <DeptGrid mt={10} py={4} display={['none', null, 'grid']}>
        {block.cards?.map((video, index) => (
          <GridItem
            as="button"
            cursor="pointer"
            onClick={() => handleClick(video)}
            textAlign="left"
            key={video._key}
            borderRadius="lg"
            colSpan={3}
            boxShadow="0px 4px 56px 0px rgba(5, 37, 77, 0.15)"
            mt={index % 2 !== 0 && 14}
            height="fit-content"
            maxWidth={['18.8rem', null, '100%']}
            overflow="hidden"
            _hover={{
              outline: '4px solid',
              outlineColor: 'brand.sunny',
            }}
          >
            <SocialGridItem
              block={{
                media: video.media,
                headline: video.headline,
                copy: video.copy,
              }}
              handleClick={handleClick}
            />
          </GridItem>
        ))}
      </DeptGrid>

      <DrawerModal isOpen={isOpen} onClose={onClose} modalWidth={1000}>
        <AspectRatio w="100%" ratio={16 / 9} mt={[8, null, 0]}>
          <>
            {isLoading && (
              <Box>
                <Spinner size="lg" color="typography.light" />
              </Box>
            )}
            <ReactPlayer
              opacity={isLoading ? '0' : '1'}
              url={currentVideo?.media?.video?.url}
              width="100%"
              height="100%"
              style={{
                borderRadius: '8px',
              }}
              onReady={() => setIsLoading(false)}
              onBufferEnd={() => setIsLoading(false)}
              onBuffer={() => setIsLoading(true)}
            />
          </>
        </AspectRatio>
      </DrawerModal>
    </Container>
  );
}

SocialGrid.propTypes = {
  block: PropTypes.shape({
    _key: PropTypes.string.isRequired,
    headline: PropTypes.string.isRequired,
    cards: PropTypes.arrayOf(
      PropTypes.shape({
        media: PropTypes.shape({
          video: PropTypes.shape({
            url: PropTypes.string,
            thumbnail: PropTypes.string,
          }),
        }),
        headline: PropTypes.string,
        copy: PropTypes.string,
      })
    ),
  }).isRequired,
  socialMedia: PropTypes.shape({
    facebookBool: PropTypes.bool,
    facebookUrl: PropTypes.string,
    instagramBool: PropTypes.bool,
    instagramUrl: PropTypes.string,
    youtubeBool: PropTypes.bool,
    youtubeUrl: PropTypes.string,
    twitterBool: PropTypes.bool,
    twitterUrl: PropTypes.string,
    pinterestBool: PropTypes.bool,
    pinterestUrl: PropTypes.string,
    tiktokBool: PropTypes.bool,
    tiktokUrl: PropTypes.string,
  }).isRequired,
};
